import { Font } from '@utmb/design-system/text/font';
import { Trans, useTranslation } from 'next-i18next';
import { ReactElement, useState } from 'react';
import { BlockDashboardWrapper } from '../my-utmb/BlockDashboardWrapper';
import { MyUtmbLabel } from '../my-utmb/MyUtmbLabel';
import styles from './my-rewards.module.scss';
import MyLoyaltyVouchers from '../my-loyalty/MyLoyaltyVouchers';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { BlockLatest } from '../my-utmb/BlockLatest';
import { MyLoyaltyHistoryRemake } from '../my-loyalty-history-remake/MyLoyaltyHistoryRemake';
import { UtmbPlusPlaceholder } from '../my-utmb/utmb-plus-placeholder/UtmbPlusPlaceholder';
import { LoyaltyHowToGainPointsModal } from '../my-utmb/loyalty-how-to-gain-points/loyalty-how-to-gain-points-modal/LoyaltyHowToGainPointsModal';
import { MyMatrixRemake } from '../my-matrix-remake/MyMatrixRemake';
import { LoyaltyPreviewContainer } from '../my-utmb/loyalty-preview-container/LoyaltyPreviewContainer';

const MyRewards = (): ReactElement => {
    const { t } = useTranslation('myutmb');
    const { dashboardMember } = useDashboard();
    const [isHowToGainPointsModalOpen, setIsHowToGainPointsModalOpen] = useState<boolean>(false);
    const isCurrentLevel = typeof dashboardMember?.loyalty?.currentLevel === 'number' && dashboardMember.loyalty.levels[0].minPoints !== -1;

    return (
        <div className={styles.container}>
            <BlockDashboardWrapper
                linkLabel={t('myUtmb.howToGainPoints.link')}
                linkUrl={() => setIsHowToGainPointsModalOpen(true)}
                variant="dark"
                className={styles.loyaltyBlock + ' ' + (isCurrentLevel ? '' : styles.loyaltyEmptyBlock)}
                label={<MyUtmbLabel forcePlus />}
            >
                {isCurrentLevel ? <LoyaltyPreviewContainer dashboardMember={dashboardMember} /> : <UtmbPlusPlaceholder />}
            </BlockDashboardWrapper>

            {(dashboardMember?.vouchersPartners?.length ?? 0) > 0 && (
                <>
                    <BlockDashboardWrapper
                        className={styles.vouchersBlock}
                        label={
                            <>
                                <Font.FuturaMedium as="span">{t('myUtmb.vouchers.title1')}</Font.FuturaMedium>
                                &nbsp;
                                <Font.FuturaBold as="span">{t('myUtmb.vouchers.title2')}</Font.FuturaBold>
                            </>
                        }
                    >
                        <MyLoyaltyVouchers />
                    </BlockDashboardWrapper>
                </>
            )}

            {dashboardMember ? (
                <BlockDashboardWrapper
                    className={styles.matrixBlock + ' ' + (isCurrentLevel ? '' : styles.matrixBlockFullWidth)}
                    linkLabel={t('myUtmb.howToGainPoints.link')}
                    linkUrl={() => setIsHowToGainPointsModalOpen(true)}
                    label={
                        <>
                            <Font.FuturaMedium as="span">{t('myUtmb.rewards.title1')}</Font.FuturaMedium>
                            &nbsp;
                            <Font.FuturaBold as="span">{t('myUtmb.rewards.title2')}</Font.FuturaBold>
                        </>
                    }
                    subtitle={
                        <>
                            <Font.FuturaMedium as="span">{t('myUtmb.rewards.subtitle1')}</Font.FuturaMedium>
                            &nbsp;
                            <Font.FuturaBold as="span">{t('myUtmb.rewards.subtitle2')}</Font.FuturaBold>
                        </>
                    }
                >
                    <MyMatrixRemake dashboardMember={dashboardMember} />
                </BlockDashboardWrapper>
            ) : null}

            {isCurrentLevel ? (
                <BlockDashboardWrapper
                    linkLabel={t('myUtmb.howToGainPoints.link')}
                    linkUrl={() => setIsHowToGainPointsModalOpen(true)}
                    className={styles.historyBlock}
                    label={
                        <>
                            <Font.FuturaMedium as="div" className={styles.subtitle}>
                                <Trans i18nKey="myutmb:myUtmb.loyalty.history.title" components={{ utmbplus: <MyUtmbLabel forcePlus /> }} />
                            </Font.FuturaMedium>
                        </>
                    }
                >
                    <MyLoyaltyHistoryRemake />
                </BlockDashboardWrapper>
            ) : null}

            <div className={styles.latestBlock}>
                <BlockLatest />
            </div>

            {dashboardMember?.loyalty ? (
                <LoyaltyHowToGainPointsModal
                    currentLevel={dashboardMember.loyalty.currentLevel}
                    loyaltyRules={dashboardMember.loyaltyRules}
                    isOpen={isHowToGainPointsModalOpen}
                    close={() => setIsHowToGainPointsModalOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default MyRewards;
